/**
 * Life4Earth V2 — React hook for loading mission metrics + targets from the API.
 *
 * Replaces: import { missionMetricsData } from './Data/KpiEngine/missionMetricsData'
 *           import { missionTargets } from './Data/KpiEngine/missionTargets'
 *
 * Returns both metric points (seed shape) and targets (seed shape) with
 * automatic fallback to the static seed data when the API is unreachable.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllMissionMetricPoints,
  fetchAllMissionTargets,
  metricPointToSeedShape,
  targetsToSeedShape,
} from "./missionMetrics";
import type {
  MissionMetricPointResponse,
  MissionTargetResponse,
} from "./missionMetrics";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

export interface UseApiMissionDataResult {
  /** Mission metric points in seed shape (project_GUID, subKpiKey, date, value, ...) */
  missionData: Record<string, any>[];
  /** Mission targets in seed shape: Record<projectGuid, Record<subKpiKey, target>> */
  missionTargetsData: Record<string, Record<string, any>>;
  /** Raw API responses */
  rawPoints: MissionMetricPointResponse[];
  rawTargets: MissionTargetResponse[];
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export function useApiMissionData(
  fallbackPointsFn?: () => any[],
  fallbackTargetsFn?: () => Record<string, Record<string, any>>
): UseApiMissionDataResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiMissionDataResult>({
    missionData: [],
    missionTargetsData: {},
    rawPoints: [],
    rawTargets: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const cached = await fetchForKey(
        QUERY_KEYS.mission,
        async () => {
          const [apiPoints, apiTargets] = await Promise.all([
            fetchAllMissionMetricPoints(),
            fetchAllMissionTargets(),
          ]);

          return {
            rawPoints: apiPoints,
            rawTargets: apiTargets,
            missionData: apiPoints.map(metricPointToSeedShape),
            missionTargetsData: targetsToSeedShape(apiTargets),
          };
        },
        {
          force: true,
          fallbackFn: () => {
            const fallbackData: any = {
              rawPoints: [],
              rawTargets: [],
              missionData: [],
              missionTargetsData: {},
            };

            if (fallbackPointsFn) {
              try {
                fallbackData.missionData = fallbackPointsFn();
              } catch {
                fallbackData.missionData = [];
              }
            }

            if (fallbackTargetsFn) {
              try {
                fallbackData.missionTargetsData = fallbackTargetsFn();
              } catch {
                fallbackData.missionTargetsData = {};
              }
            }

            return fallbackData;
          },
        }
      );

      if (mountedRef.current) {
        setData({
          missionData: cached.missionData,
          missionTargetsData: cached.missionTargetsData,
          rawPoints: cached.rawPoints,
          rawTargets: cached.rawTargets,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          missionData: [],
          missionTargetsData: {},
          rawPoints: [],
          rawTargets: [],
          loading: false,
          error: err.message,
          source: "seed",
          refetch,
        });
      }
    }
  }, [fallbackPointsFn, fallbackTargetsFn]);

  useEffect(() => {
    mountedRef.current = true;

    async function load() {
      try {
        const cached = await fetchForKey(
          QUERY_KEYS.mission,
          async () => {
            const [apiPoints, apiTargets] = await Promise.all([
              fetchAllMissionMetricPoints(),
              fetchAllMissionTargets(),
            ]);

            return {
              rawPoints: apiPoints,
              rawTargets: apiTargets,
              missionData: apiPoints.map(metricPointToSeedShape),
              missionTargetsData: targetsToSeedShape(apiTargets),
            };
          },
          {
            fallbackFn: () => {
              const fallbackData: any = {
                rawPoints: [],
                rawTargets: [],
                missionData: [],
                missionTargetsData: {},
              };

              if (fallbackPointsFn) {
                try {
                  fallbackData.missionData = fallbackPointsFn();
                } catch {
                  fallbackData.missionData = [];
                }
              }

              if (fallbackTargetsFn) {
                try {
                  fallbackData.missionTargetsData = fallbackTargetsFn();
                } catch {
                  fallbackData.missionTargetsData = {};
                }
              }

              return fallbackData;
            },
          }
        );

        if (mountedRef.current) {
          setData({
            missionData: cached.missionData,
            missionTargetsData: cached.missionTargetsData,
            rawPoints: cached.rawPoints,
            rawTargets: cached.rawTargets,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            missionData: [],
            missionTargetsData: {},
            rawPoints: [],
            rawTargets: [],
            loading: false,
            error: err.message,
            source: "seed",
            refetch,
          });
        }
      }
    }

    load();

    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === QUERY_KEYS.mission) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [refetch, fallbackPointsFn, fallbackTargetsFn]);

  return data;
}
